import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  MessageStatus
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { IconButton } from '@material-ui/core';
import { CircleArrowLeft } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import ReactHelmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { Loader } from '../../../../../../components/Loader';
import { Spacer } from '../../../../../../components/Spacer';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useRouter } from '../../../../../../hooks/useRouter';
import { Centered } from '../../../../../../layout/Centered';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import {
  useNumberQueryParam,
  useStringQueryParam,
  useTypedStringQueryParam
} from '../../../../../../routes';
import { useTaskCompletionSuccessNotifications } from '../../../../../features/Experience/service';
import { useProductAnalytics } from '../../../../../features/Experience/services/productAnalytics';
import { useStandaloneAppThemeOverride } from '../../../../../hooks/useStandaloneAppContext';
import { WithBreakpoints } from '../../components/Breakpoints';
import { MessageCard } from '../../components/Messages';
import { InboxNavItemKey, InboxStyleNav } from '../../components/Nav/InboxNav';
import {
  getStartingWeek,
  groupWeeks,
  WeekSelector
} from '../../components/Nav/WeekNav';
import {
  PageContainer,
  PageContainerInner,
  PageFrame,
  PageFrameHeader
} from '../../components/PageFrame';
import { ParticipantAwareTitle } from '../../components/ParticipantAwareTitle';
import { ProgressTracker } from '../../components/ProgressTracker';
import {
  getUrlForItem,
  SearchWithinInstanceButton
} from '../../components/Search/WithinInstance';
import { TopNavBarV2ForInstance } from '../../components/TopNav';
import { useEmployeeAppExperienceInstance } from '../../services/retrieval';
import { getIndividualInstanceUrl } from '../../services/routes';

const Container = styled('div')((p) => ({
  width: '100%'
}));

const Main = ({
  instance,
  message,
  actorAccessToken,
  instanceAccessToken,
  archiveControls,
  returnTo
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  message: IExperienceMessageDenormalized;
  actorAccessToken: string;
  instanceAccessToken: string;
  archiveControls?: boolean;
  returnTo?: string;
}) => {
  // const { trackEvent } = useProductAnalytics(instanceAccessToken);

  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between tasks, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [message.templateId]);

  const backUrl =
    returnTo || getIndividualInstanceUrl(actorAccessToken, instanceAccessToken);

  return (
    <Container ref={ref}>
      <FlexContainerVertical fullWidth spacing={3}>
        <FlexContainerVertical fullWidth spacing={2}>
          <Link
            to={backUrl}
            onClick={() =>
              // trackEvent({
              //   eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
              //   name: 'Back Link Clicked',
              //   Explanation:
              //     'A user clicked the back link at the top of the single task page',
              //   'Message ID': message.id,
              //   'Back URL': backUrl
              // })
              undefined
            }
          >
            <IconButton color="primary">
              <CircleArrowLeft size={24} />
            </IconButton>
          </Link>
          <MessageCard
            d={message}
            instanceAccessToken={instanceAccessToken}
            archiveControls={archiveControls}
            alwaysExpand
          />
        </FlexContainerVertical>
      </FlexContainerVertical>
    </Container>
  );
};

type BodyProps = {
  instance: Doc<IExperienceDenormalizedV2>;
  messageId: string;
  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
};

const BodyInboxNavStyle = ({
  instance,
  messageId,
  actorAccessToken,
  instanceAccessToken,
  returnTo
}: BodyProps) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const message = useMemo(() => {
    return instance.data.messages.find((msg) => msg.templateId === messageId);
  }, [instance.data.messages, messageId]);

  // TODO look at archive status
  const [selectedGroup] = useTypedStringQueryParam<InboxNavItemKey>(
    'g',
    message?.status === MessageStatus.DONE ||
      message?.status === MessageStatus.SKIPPED
      ? 'msgs'
      : message?.status === MessageStatus.UPCOMING
      ? 'upcoming-msgs'
      : 'everything'
  );

  // useEffect(() => {
  //   trackEvent({
  //     eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
  //     name: 'Single Task Page Loaded',
  //     Explanation: 'A user opened a task page',
  //     'Task ID': messageId,
  //     View: 'Instance',
  //     'Message present': messagePresent
  //   });
  // }, [trackEvent, messageId, messagePresent]);

  const { history } = useRouter();

  if (!message) {
    return <Centered>Message not found!</Centered>;
  }

  return (
    <PageContainer>
      <PageContainerInner>
        <TopNavBarV2ForInstance
          instance={instance}
          searchComponent={
            <SearchWithinInstanceButton
              allowGlobalSearch
              instance={instance}
              onSelectMatch={(item) => {
                const url = getUrlForItem(
                  item,
                  actorAccessToken,
                  instanceAccessToken,
                  returnTo
                );
                if (url) {
                  history.push(url);
                }
              }}
              closeOnSelect
            />
          }
        />
        <PageFrame
          aiDisclosure={instance.data.aiDisclosure}
          banner={instance.data.theming.banner}
          header={
            <PageFrameHeader>
              <ParticipantAwareTitle d={instance} />
              <ProgressTracker d={instance.data.progress} />
            </PageFrameHeader>
          }
          left={
            <InboxStyleNav
              actorAccessToken={actorAccessToken}
              instanceAccessToken={instanceAccessToken}
              instance={instance}
              g={selectedGroup}
              trackFn={trackEvent}
            />
          }
          center={
            <>
              <Main
                instance={instance}
                message={message}
                actorAccessToken={actorAccessToken}
                instanceAccessToken={instanceAccessToken}
                returnTo={returnTo}
                archiveControls
              />
              <WithBreakpoints lg={<Spacer height={8} />} />
            </>
          }
          right={<div />}
        />
      </PageContainerInner>
    </PageContainer>
  );
};

const BodyWeekNavStyle = ({
  instance,
  messageId,
  actorAccessToken,
  instanceAccessToken,
  returnTo
}: BodyProps) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const weekGroups = useMemo(() => groupWeeks(instance), [instance]);
  const n = useMemo(() => moment(), []);
  const currentWeek = useMemo(
    () => getStartingWeek(instance, weekGroups, n),
    []
  );
  const [selectedWeek] = useNumberQueryParam('w', currentWeek);
  const message = useMemo(() => {
    return instance.data.messages.find((msg) => msg.templateId === messageId);
  }, [instance.data.messages, messageId]);

  console.log('MSG', message, instance.data.messages, messageId);

  // useEffect(() => {
  //   trackEvent({
  //     eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
  //     name: 'Single Task Page Loaded',
  //     Explanation: 'A user opened a task page',
  //     'Task ID': taskId,
  //     View: 'Instance',
  //     'Message present': messagePresent
  //   });
  // }, [trackEvent, taskId, messagePresent]);

  const { history } = useRouter();

  if (!message) {
    return <Centered>Message not found!</Centered>;
  }

  return (
    <PageContainer>
      <PageContainerInner>
        <TopNavBarV2ForInstance
          instance={instance}
          searchComponent={
            <SearchWithinInstanceButton
              allowGlobalSearch
              instance={instance}
              onSelectMatch={(item) => {
                const url = getUrlForItem(
                  item,
                  actorAccessToken,
                  instanceAccessToken,
                  returnTo
                );
                if (url) {
                  history.push(url);
                }
              }}
              closeOnSelect
            />
          }
        />
        <PageFrame
          aiDisclosure={instance.data.aiDisclosure}
          banner={instance.data.theming.banner}
          header={
            <PageFrameHeader>
              <ParticipantAwareTitle d={instance} />
              <ProgressTracker d={instance.data.progress} />
            </PageFrameHeader>
          }
          left={
            <WeekSelector
              instance={instance}
              weekGroups={weekGroups}
              currentWeek={currentWeek}
              selectedWeek={selectedWeek}
              setSelectedWeekToUrl={(w) =>
                getIndividualInstanceUrl(
                  actorAccessToken,
                  instanceAccessToken,
                  { w }
                )
              }
              n={n}
              trackFn={trackEvent}
            />
          }
          center={
            <>
              <Main
                instance={instance}
                message={message}
                actorAccessToken={actorAccessToken}
                instanceAccessToken={instanceAccessToken}
                returnTo={returnTo}
                archiveControls
              />
              <WithBreakpoints lg={<Spacer height={8} />} />
            </>
          }
          right={<div />}
        />
      </PageContainerInner>
    </PageContainer>
  );
};

const Body = (props: BodyProps) => {
  if (props.instance.data.instancePageNavStyle === 'INBOX') {
    return <BodyInboxNavStyle {...props} />;
  }
  return <BodyWeekNavStyle {...props} />;
};

export const EmployeeAppPageExperienceMessageDetailsWithData = ({
  instance,
  actorAccessToken,
  instanceAccessToken,
  messageId
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  actorAccessToken: string;
  instanceAccessToken: string;
  messageId: string;
}) => {
  const [returnTo] = useStringQueryParam('returnTo');
  useStandaloneAppThemeOverride(instance?.data.org.experienceTheming || null);
  useTaskCompletionSuccessNotifications(instance);

  console.log('INSTANCE', instance, messageId);
  return (
    <>
      <ReactHelmet>
        <title>{instance.data.title}</title>
      </ReactHelmet>
      <Body
        instance={instance}
        messageId={messageId}
        actorAccessToken={actorAccessToken}
        instanceAccessToken={instanceAccessToken}
        returnTo={returnTo}
      />
    </>
  );
};

export const EmployeeAppPageExperienceMessageDetails = ({
  actorAccessToken,
  instanceAccessToken,
  messageId,
  v
}: {
  actorAccessToken: string;
  instanceAccessToken: string;
  messageId: string;
  v: number;
}) => {
  const [instance, loading, error] = useEmployeeAppExperienceInstance(
    actorAccessToken,
    instanceAccessToken,
    v
  );
  useErrorLogger('useEmployeeAppExperienceInstance', error);

  if (instance) {
    return (
      <EmployeeAppPageExperienceMessageDetailsWithData
        instance={instance}
        messageId={messageId}
        actorAccessToken={actorAccessToken}
        instanceAccessToken={instanceAccessToken}
      />
    );
  }

  // TODO error
  return <Loader />;
};
